import { el } from "../lib/dom.js";

/** How long routine feedback stays, counted only while the page is visible. */
export const FEEDBACK_MS = 4000;

export interface FeedbackLine {
  node: HTMLElement;
  /** Shows `text` in place of whatever the line held, and starts its time again. */
  show(text: string): void;
  clear(): void;
  dispose(): void;
}

/**
 * A screen's routine feedback (g102): one line of words in the normal layout,
 * where the toast used to float over the list.
 *
 * It says that something ordinary happened and then goes, after four seconds
 * of visible time. Nothing depends on reading it in time: it holds no Undo and
 * no Dismiss, takes no focus, and the command it once carried lives in the
 * screen's menu for as long as it applies. A new message replaces the last
 * one, and text that has expired is gone: the page coming back from the
 * background, or a field letting go of the keyboard, brings nothing back.
 *
 * It has no live region of its own; the screen says the same words through
 * the one it already has. An error is not routine and never comes here.
 */
export function feedbackLine(options: {
  class?: string;
  /** Wraps each change of text, so the screen can hold its place while the line comes and goes. */
  around?(change: () => void): void;
  /** Called after the line fills or empties. */
  changed?(shown: boolean): void;
} = {}): FeedbackLine {
  const node = el("p", { class: options.class ?? "feedback" });
  let timer: ReturnType<typeof setTimeout> | undefined;
  let remaining = 0;
  let started = 0;
  let disposed = false;

  const visible = () => document.visibilityState === "visible";

  function write(text: string): void {
    if (node.textContent === text) return;
    const change = () => { node.textContent = text; };
    if (options.around) options.around(change);
    else change();
    options.changed?.(text !== "");
  }

  function stop(): void {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  }

  function run(): void {
    stop();
    if (remaining <= 0 || !visible()) return;
    started = Date.now();
    timer = setTimeout(clear, remaining);
  }

  function clear(): void {
    stop();
    remaining = 0;
    write("");
  }

  // Hidden time is not reading time: the count stops with the page and picks
  // up what was left of it.
  const onVisibility = () => {
    if (remaining <= 0) return;
    if (visible()) { run(); return; }
    if (timer === undefined) return;
    remaining = Math.max(1, remaining - (Date.now() - started));
    stop();
  };
  document.addEventListener("visibilitychange", onVisibility);

  return {
    node,
    show(text) {
      if (disposed) return;
      write(text);
      remaining = FEEDBACK_MS;
      run();
    },
    clear,
    dispose() {
      disposed = true;
      clear();
      document.removeEventListener("visibilitychange", onVisibility);
    },
  };
}

/**
 * Runs `change`, which fills or empties `line`, and holds the page still
 * when the line is scrolled out of sight above: its height would otherwise
 * shift every row the person is reading. A line on screen is left to take
 * its room, since that is the change being shown, and the header it sits
 * under stays where it is.
 *
 * Chrome's own scroll anchoring would decide otherwise in both cases: it
 * holds the page to a row, which corrects the first shift a second time and
 * scrolls the header away for the second. So nothing in the screen is an
 * anchor until the change has been laid out, two frames on, as a reorder of
 * the rows does it; Safari has none to switch off. The property goes on the
 * screen and not the root: on the root, Chrome still moved the page.
 */
let unanchored = 0;
let scoped: HTMLElement | undefined;
export function holdingPlace(line: HTMLElement, rows: HTMLElement, change: () => void): void {
  const above = () => {
    const box = line.getBoundingClientRect();
    return box.height > 0 && box.bottom <= 0;
  };
  const anchor = [...rows.children].find(row => row.getBoundingClientRect().bottom > 0);
  const before = anchor?.getBoundingClientRect().top;
  const was = above();
  const scope = line.parentElement ?? document.documentElement;
  if (scoped !== scope) scoped?.style.removeProperty("overflow-anchor");
  scoped = scope;
  scope.style.setProperty("overflow-anchor", "none");
  cancelAnimationFrame(unanchored);
  unanchored = requestAnimationFrame(() => {
    unanchored = requestAnimationFrame(() => { scope.style.removeProperty("overflow-anchor"); scoped = undefined; });
  });
  change();
  if (!(was || above()) || !anchor || before === undefined || !anchor.isConnected) return;
  const drift = anchor.getBoundingClientRect().top - before;
  if (drift !== 0) window.scrollBy(0, drift);
}
